import { TableProps } from "../../../types"
import React, { FC, useEffect, useRef, useState } from "react"
import { isEqual } from "lodash"
import { KButton, KInput, KSpan, KTitleSpan } from "kahuna-base-react-components"
import ReportListComponent from "./ReportListComponent"
import NotFound from "../../NotFound"
import { lang } from "../../../constants/languages"
import { connect } from "react-redux"
//@ts-ignore
import DownloadIcon from "../../../assets/release-registry-icons/download.svg"
import { users } from "../../../apis"
import JSZip from "jszip"
import { saveAs } from "file-saver"
import { errorFilledToastOptions, infoFilledToastOptions } from "../../../constants"
import { toast } from "react-toastify"
import Loading from "../Loading"
import { RootState } from "../../../store"

export interface ReportData {
  quarter: string
  revenue: number
  user_payment_total: number
  user_payment_details: {
    regular: number // the amount which is deducted from advance
    advance: number // the amount which is directly paid
    advance_payment_list: { date: string; payment_amount: number }[]
    regular_payment_list: { date: string; payment_amount: number }[]
  }
  currency: string
  index: number
  payment_day?: string[]
  revenue_details: { [key: string]: number }
  advance_paid: number
  advance_paid_list: { date: string; advance_paid: number; advance_type: string }[]
  balance_remaining?: number
  advance_currency?: string
}
const ReportsTable: FC<TableProps> = (props) => {
  const { title, data, count, loading, setTableAPI, hidden, additionalFilters, isAdmin, selectedAccountType } = props

  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState<{ [key: string]: string | number | boolean }>({})
  const [searchText, setSearchText] = useState<string>("")
  const [filteredData, setFilteredData] = useState<ReportData[]>(data)
  const additionalFiltersRef = useRef(additionalFilters)
  const [advancePaid, setAdvancePaid] = useState<boolean>(false)
  const [downloading, setDownloading] = useState<boolean>(false)

  const handleChange = (newValue: string) => {
    setSearchText(newValue)
  }

  const filterDataBySearchText = (data: ReportData[], searchText: string): ReportData[] => {
    if (data.length > 0) {
      return data.filter((item) => {
        return item.quarter.toLowerCase().includes(searchText.toLowerCase())
      })
    } else {
      return []
    }
  }

  const onDownloadAll = async () => {
    setDownloading(true)
    users(`/user_report_download_all/?selected_account=${selectedAccountType}`, "get").then(async (res) => {
      if (!res.data.results || res.data.results.length === 0) {
        toast.error(lang.reports.list_component.link_not_found, errorFilledToastOptions)
        setDownloading(false)
        return
      }
      const zip = new JSZip()
      const totalCount = res.data.count
      let counter = 1
      for (const currentUser of res.data.results) {
        const folder = zip.folder(currentUser.name)

        for (const url of currentUser.report_list) {
          if (counter % 5 === 0) {
            toast.info(`${lang.reports.downloading}... ${counter}/${totalCount}`, infoFilledToastOptions)
          }
          counter += 1
          const response = await fetch(url)

          if (props.user?.hideReportTotalAmount) {
            const text = await response.text()

            const rows = text
              .trim()
              .split("\n")
              .map((r) => r.split(","))

            const header = rows[0]
            const totalAmountIndex = header.indexOf("Total Amount")

            let newRows = rows
            if (totalAmountIndex !== -1) {
              newRows = rows.map((row) => row.filter((_, i) => i !== totalAmountIndex))
            }

            const newCsv = newRows.map((r) => r.join(",")).join("\n")

            const fileName = url.split("/").pop()
            if (folder) folder.file(fileName, newCsv)
          } else {
            const blob = await response.blob()

            const fileName = url.split("/").pop()
            if (folder) folder.file(fileName, blob)
          }
        }
      }

      const content = await zip.generateAsync({ type: "blob" })
      saveAs(content, "reports.zip")

      setDownloading(false)
    })
  }

  useEffect(() => {
    const filterData = filterDataBySearchText(data, searchText)
    setFilteredData(filterData)

    if (data && Array.isArray(data)) {
      const hasAdvancePaid = data?.some((item) => item.advance_paid !== undefined && item.advance_paid > 0)
      setAdvancePaid(hasAdvancePaid)
    }
  }, [searchText, data])

  useEffect(() => {
    if (setTableAPI) setTableAPI({ currentPage, setCurrentPage })
  }, [currentPage, count])

  useEffect(() => {
    if (!isEqual(additionalFiltersRef.current, additionalFilters)) {
      const newFilters = { ...filters, ...additionalFilters }
      setFilters(newFilters)
      additionalFiltersRef.current = additionalFilters
    }
  }, [additionalFilters])

  if (hidden) return null

  return (
    <div>
      {downloading && <Loading />}
      <div className={`flex flex-row justify-between items-center ${isAdmin ? "py-4" : "py-6 px-3"}`}>
        <div className="flex flex-row items-center justify-start gap-2">
          <div className="flex items-center pt-1">
            <KTitleSpan
              text={title?.toString() || ""}
              fontSize={20}
              color="#111"
              lineHeight={"28px"}
              fontWeight={500}
            />
          </div>
        </div>
        <div className="flex gap-4">
          <KButton
            shadowDisabled
            width="160px"
            onClick={() => onDownloadAll()}
            gap="8px"
            leftIcon={DownloadIcon}
            text={lang.reports.download_all}
            background="white"
          />
          <div style={{ width: 260 }}>
            <KInput
              dataTestId="reports-search-input"
              onChange={handleChange}
              value={searchText}
              placeholder={lang.reports.search_text_placeholder}
              padding="14px"
              leftIcon="/catalog_icons/search-grey.svg"
              hoverBackground="white"
              background="#f5f5f5"
            />
          </div>
        </div>
      </div>
      <div className={`mt-6 bg-white elev-b-sm rounded-lg w-full shadow-none`}>
        {!loading && filteredData.length > 0 && (
          <div className="flex flex-row justify-between items-center h-[52px] py-2 px-3">
            <div className="flex w-[20%] items-center justify-start">
              <KSpan text={lang.reports.list_component.report} fontWeight={500} color="#000" />
            </div>
            <div className="flex w-[15%] items-center justify-start">
              <KSpan text={lang.reports.list_component.revenue} fontWeight={500} color="#000" />
            </div>
            <div className="flex w-[15%] items-center justify-start">
              <KSpan text={lang.reports.list_component.total_payment} fontWeight={500} color="#000" />
            </div>
            <div className="flex w-[15%] items-center justify-start">
              <KSpan text={lang.reports.list_component.remaining_payment} fontWeight={500} color="#000" />
            </div>
            <div className="flex w-[15%] items-center justify-start">
              <KSpan text={lang.reports.list_component.payment_date} fontWeight={500} color="#000" />
            </div>
            {advancePaid && (
              <div className="flex w-[10%] items-center justify-start">
                <KSpan text={lang.reports.list_component.advance_paid} fontWeight={500} color="#000" />
              </div>
            )}
            {advancePaid && (
              <div className="flex w-[15%] items-center justify-start">
                <KSpan text={lang.reports.list_component.balance_remaining} fontWeight={500} color="#000" />
              </div>
            )}
            <div className="flex w-[5%] items-center justify-start">
              <KSpan text={""} fontWeight={500} color="#000" />
            </div>
          </div>
        )}

        <ul>
          {!loading &&
            filteredData?.map((row, i) => {
              return (
                <ReportListComponent
                  key={`report-list-component-${i}`}
                  i={i}
                  row={row}
                  isAdmin={props.isAdmin}
                  selectedUserId={props.selectedUserId}
                  approveDisabled={props.approveDisabled}
                  advancePaid={advancePaid}
                />
              )
            })}
          {!loading && filteredData.length === 0 && (
            <div className="flex justify-center mt-12">
              <NotFound
                title={lang.not_found.reports.title}
                description={lang.not_found.reports.desc}
                imageWidth={225}
              />
            </div>
          )}
          {isAdmin && !props.selectedUserId && (
            <div className="flex justify-center mt-12">
              <NotFound
                title={lang.not_found.reports.title}
                description={"No data available. Please select a user to view details."}
                imageWidth={225}
              />
            </div>
          )}
        </ul>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(ReportsTable)
